import { useEffect, useState, useRef, useCallback } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Search,
  X,
  User as UserIcon,
  Phone,
  Hash,
  MapPin,
  ArrowLeft,
  CheckCircle2,
  History as HistoryIcon,
  Star,
  Pause,
  BarChart3,
  Receipt,
  UserPlus,
  Building2,
  Repeat2,
  Pencil,
  Plus,
  Keyboard as KeyboardIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useKeyboard } from "@/lib/KeyboardContext";
import Numpad from "@/components/Numpad";
import { CustomerDialog } from "@/pages/Customers";

/**
 * «Επιλογή Πελάτη» — full-screen, touch-friendly customer picker for the
 * Delivery «Νέα Παραγγελία» editor.
 *
 *  Two-step flow inside one screen:
 *    Step 1 (search):  big input + result cards (rich, 110-130px tall)
 *    Step 2 (confirm): customer card with address radio picker +
 *                      «Ιστορικό» tab. Cashier picks default or any
 *                      other address, then [Επιβεβαίωση] returns the
 *                      pair (customer, address) to the editor.
 *
 *  The keyboard toggle button inside this dialog flips the global
 *  on-screen keyboard so a tablet operator never needs a physical one.
 */
export default function DeliveryCustomerDialog({ open, onOpenChange, onSelect, onAddItem, initialCustomerId = null }) {
  const { open: kbOpen, toggle: kbToggle } = useKeyboard();
  const [step, setStep] = useState("search");        // 'search' | 'confirm'
  // When the editor passes an initialCustomerId, we jump straight to
  // the «κάρτα πελάτη» (confirm step). The "← Αναζήτηση" button on the
  // confirm header acts as the «Αλλαγή Πελάτη» action.
  const isProfileMode = !!initialCustomerId;
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [activeAddrId, setActiveAddrId] = useState(null);
  const [history, setHistory] = useState(null);
  const [topItems, setTopItems] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  // Receipt preview (history tab click). Holds the receipt log row with items.
  const [previewReceipt, setPreviewReceipt] = useState(null);
  // In-dialog numpad — for searching by phone/code without a physical kb.
  const [numpad, setNumpad] = useState({ open: false, value: "" });
  // Inline customer-card editor. `mode = "new" | "edit"`; when set,
  // overlays the full-screen CustomerDialog without leaving Delivery.
  const [cardEdit, setCardEdit] = useState({ open: false, customer: null });
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setSearched(false);
      setActiveAddrId(null);
      setHistory(null);
      setTopItems(null);
      setLastOrder(null);
      setPreviewReceipt(null);
      setNumpad({ open: false, value: "" });
      if (initialCustomerId) {
        // Profile mode — keep step on "confirm" with the supplied
        // customer pre-loaded; avoids flashing the search screen.
        setStep("confirm");
        (async () => {
          try {
            const r = await http.get(`/customers/${initialCustomerId}`);
            await pickCustomer(r.data);
          } catch (e) {
            setStep("search");
          }
        })();
      } else {
        setStep("search");
        setActiveCustomer(null);
        setTimeout(() => inputRef.current?.focus(), 80);
      }
    }
  }, [open, initialCustomerId]);

  const runSearch = useCallback(async (term) => {
    setBusy(true);
    setSearched(true);
    try {
      const digits = term.replace(/\D/g, "");
      // 6-digit code → exact lookup.
      if (/^\d{6}$/.test(term)) {
        const r = await http.get(`/customers/lookup-by-code?code=${digits}`);
        setResults(r.data.found ? [r.data.customer] : []);
        return;
      }
      if (/^\d{10}$/.test(digits)) {
        const r = await http.get(`/customers/lookup-by-phone?phone=${digits}`);
        if (r.data.found) { setResults([r.data.customer]); return; }
      }
      const r = await http.get(`/customers?q=${encodeURIComponent(term)}&limit=30`);
      setResults(r.data?.items || []);
    } catch (e) {
      toast.error("Σφάλμα αναζήτησης");
    } finally {
      setBusy(false);
    }
  }, []);

  // Debounced live-search
  useEffect(() => {
    if (!open || step !== "search") return;
    if (!q.trim()) { setResults([]); setSearched(false); return; }
    const t = setTimeout(() => runSearch(q.trim()), 280);
    return () => clearTimeout(t);
  }, [q, open, step, runSearch]);

  const pickCustomer = async (c) => {
    setBusy(true);
    try {
      // Refetch the full customer doc so we have all addresses,
      // even if the search returned a slim shape.
      const full = await http.get(`/customers/${c.id}`);
      const cust = full.data;
      setActiveCustomer(cust);
      const def = (cust.addresses || []).find((a) => a.is_default && a.status === "active")
        || (cust.addresses || []).find((a) => a.status === "active")
        || (cust.addresses || [])[0];
      setActiveAddrId(def?.id || null);
      // Fire-and-forget enrichment for the «Ιστορικό» tab + last order
      // quick-repeat surface.
      http.get(`/customers/${c.id}/history?limit=20`).then((r) => setHistory(r.data)).catch(() => setHistory({ items: [] }));
      http.get(`/customers/${c.id}/top-items?limit=5`).then((r) => setTopItems(r.data)).catch(() => setTopItems({ items: [] }));
      http.get(`/customers/${c.id}/last-order`).then((r) => setLastOrder(r.data)).catch(() => setLastOrder({ found: false }));
      setStep("confirm");
    } finally {
      setBusy(false);
    }
  };

  const confirm = (opts = {}) => {
    if (!activeCustomer) return;
    const addr = (activeCustomer.addresses || []).find((a) => a.id === activeAddrId) || null;
    onSelect?.({
      customer: activeCustomer,
      address: addr,
      // When the cashier hit «Επανάληψη Τελευταίας», the editor receives
      // the last-order items and seeds the cart.
      lastOrder: opts.repeat && lastOrder?.found ? lastOrder : null,
    });
    onOpenChange(false);
  };

  const createNew = () => {
    // Pre-seed phone if the search term looks like a 10-digit phone.
    const phoneDigits = q.replace(/\D/g, "");
    const seedPhone = /^\d{10}$/.test(phoneDigits) ? phoneDigits : "";
    // Open the full-screen CustomerDialog inline (overlay) — no
    // navigation away from the Delivery flow.
    setCardEdit({
      open: true,
      customer: seedPhone ? { phones: [seedPhone] } : null,
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-slate-100 flex flex-col" data-testid="delivery-customer-dialog">
      {/* ============ STEP 1: SEARCH ============ */}
      {step === "search" && (
        <>
          <div className="bg-white border-b-2 border-slate-300 px-5 py-3 flex items-center gap-3 shrink-0">
            <Button
              variant="outline"
              size="lg"
              onClick={() => onOpenChange(false)}
              className="h-12 px-4 border-2"
              data-testid="delivery-customer-close"
            >
              <X size={18} className="mr-1" /> Κλείσιμο
            </Button>
            <div className="flex-1">
              <h2 className="text-2xl font-heading font-bold leading-tight">Επιλογή Πελάτη</h2>
              <div className="text-xs text-slate-500">Όνομα, διεύθυνση, τηλέφωνο ή κωδικός (6 ψηφία)</div>
            </div>
            <Button
              size="lg"
              onClick={createNew}
              className="h-12 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="delivery-customer-new-top"
              title="Δημιουργία νέου πελάτη"
            >
              <UserPlus size={18} className="mr-1.5" /> Νέος Πελάτης
            </Button>
          </div>

          <div className="px-6 pt-5 pb-3 shrink-0 bg-slate-100">
            <div className="max-w-4xl mx-auto flex items-stretch gap-2">
              <div className="relative flex-1">
                <Search size={26} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="π.χ. Παπαδ, 6912345678, 000023..."
                  className="pl-14 h-20 text-3xl font-bold rounded-xl border-2 shadow-sm"
                  data-testid="delivery-customer-search"
                  autoFocus
                />
                {q && (
                  <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full hover:bg-slate-200 grid place-items-center">
                    <X size={22} />
                  </button>
                )}
              </div>
              <Button
                size="lg"
                variant="outline"
                className="h-20 px-5 border-2 text-lg font-bold"
                onClick={() => setNumpad({ open: true, value: q.replace(/\D/g, "") })}
                data-testid="delivery-customer-numpad-toggle"
                title="Αριθμητικό πληκτρολόγιο"
              >
                <Hash size={22} className="mr-1" /> 123
              </Button>
              <Button
                size="lg"
                variant={kbOpen ? "default" : "outline"}
                onClick={kbToggle}
                className={`h-20 px-5 border-2 text-lg font-bold ${kbOpen ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                data-testid="delivery-customer-kb-toggle"
                title="Πληκτρολόγιο οθόνης"
              >
                <KeyboardIcon size={22} />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <div className="max-w-3xl mx-auto space-y-3">
              {busy && <div className="text-center text-slate-400 italic py-8">Αναζήτηση...</div>}
              {!busy && results.length === 0 && searched && (
                <Card className="p-6 bg-amber-50 border-amber-300 space-y-3" data-testid="delivery-customer-empty">
                  <div className="text-lg text-amber-900 font-bold">Δεν βρέθηκε πελάτης για «{q}»</div>
                  <Button onClick={createNew} size="lg" className="bg-emerald-600 hover:bg-emerald-700" data-testid="delivery-customer-new">
                    <UserPlus size={18} className="mr-2" /> Νέος Πελάτης
                  </Button>
                </Card>
              )}
              {!busy && results.length === 0 && !searched && (
                <div className="text-center text-slate-400 italic py-16 text-lg">
                  Πληκτρολογήστε για αναζήτηση
                </div>
              )}
              {results.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pickCustomer(c)}
                  className="w-full text-left bg-white hover:bg-blue-50 border-2 border-slate-200 hover:border-blue-500 rounded-xl px-5 py-4 transition-all flex items-stretch gap-4 shadow-sm"
                  data-testid={`delivery-customer-row-${c.id}`}
                >
                  {/* Code badge */}
                  <div className="flex flex-col items-center justify-center w-20 bg-blue-50 rounded-lg border-2 border-blue-200 shrink-0">
                    <Hash size={14} className="text-blue-500" />
                    <span className="text-blue-700 font-mono text-base font-bold">{c.code || "—"}</span>
                  </div>

                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-xl font-bold text-slate-900 truncate flex items-center gap-2">
                      {c.name}
                      {c.vat_no && <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600">ΑΦΜ {c.vat_no}</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                      {(c.phones || []).map((p, i) => (
                        <span key={i} className="font-mono flex items-center gap-1">
                          <Phone size={12} /> {p}
                        </span>
                      ))}
                      {c.profession && (
                        <span className="flex items-center gap-1 text-slate-500">
                          <Building2 size={12} /> {c.profession}
                        </span>
                      )}
                    </div>
                    {/* Addresses inline preview */}
                    {(c.addresses || []).filter((a) => a.status === "active").length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {(c.addresses || [])
                          .filter((a) => a.status === "active")
                          .slice(0, 3)
                          .map((a, i) => {
                            const txt = [a.street && `${a.street}${a.street_no ? " " + a.street_no : ""}`, a.area].filter(Boolean).join(", ") || "—";
                            return (
                              <span key={i} className={`text-xs px-2 py-0.5 rounded font-medium ${a.is_default ? "bg-blue-100 text-blue-800 border border-blue-300" : "bg-slate-100 text-slate-700"}`}>
                                {a.is_default && <Star size={9} className="inline mr-0.5" />}
                                {txt}
                              </span>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  {/* Stats column */}
                  <div className="text-right shrink-0 flex flex-col justify-center">
                    <div className="text-lg text-emerald-700 font-bold font-mono">{Number(c.total_spent || 0).toFixed(2)}€</div>
                    <div className="text-xs text-slate-500">{c.orders_count || 0} παραγγ.</div>
                    {c.last_order_at && (
                      <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                        {String(c.last_order_at).slice(0, 10)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ============ STEP 2: CONFIRM (loading placeholder) ============ */}
      {step === "confirm" && !activeCustomer && (
        <div className="flex-1 grid place-items-center text-slate-400 text-lg italic">
          Φόρτωση καρτέλας πελάτη…
        </div>
      )}

      {/* ============ STEP 2: CONFIRM ============ */}
      {step === "confirm" && activeCustomer && (
        <>
          <div className="bg-white border-b-2 border-slate-300 px-5 py-3 flex items-center gap-3 shrink-0">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setStep("search")}
              className={`h-12 px-4 border-2 ${isProfileMode ? "border-blue-400 text-blue-700 hover:bg-blue-50 font-bold" : ""}`}
              data-testid="delivery-customer-back"
            >
              <ArrowLeft size={18} className="mr-1" /> {isProfileMode ? "Αλλαγή Πελάτη" : "Αναζήτηση"}
            </Button>
            <div className="flex-1 min-w-0">
              <h2 className="text-3xl font-heading font-bold leading-tight truncate">{activeCustomer.name}</h2>
              <div className="text-sm text-slate-500 flex items-center gap-3 font-mono">
                <span>#{activeCustomer.code || "—"}</span>
                {activeCustomer.vat_no && <span>ΑΦΜ {activeCustomer.vat_no}</span>}
                {activeCustomer.doy && <span>{activeCustomer.doy}</span>}
              </div>
            </div>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setCardEdit({ open: true, customer: activeCustomer })}
              className="h-12 px-4 border-2 border-blue-400 text-blue-700 hover:bg-blue-50 font-bold"
              data-testid="delivery-customer-edit"
              title="Επεξεργασία καρτέλας πελάτη"
            >
              <Pencil size={18} className="mr-1.5" /> Επεξεργασία
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 bg-slate-50">
            <div className="max-w-4xl mx-auto space-y-4">
              {/* Contact info card */}
              <Card className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Τηλέφωνα</div>
                    <div className="font-mono text-base mt-1 space-y-0.5">
                      {(activeCustomer.phones || []).length > 0
                        ? (activeCustomer.phones || []).map((p, i) => <div key={i}>{p}</div>)
                        : <span className="text-slate-400">—</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Επάγγελμα</div>
                    <div className="text-base mt-1">{activeCustomer.profession || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Σύνολο</div>
                    <div className="text-base font-bold text-emerald-700 mt-1">{Number(activeCustomer.total_spent || 0).toFixed(2)}€</div>
                    <div className="text-xs text-slate-500">{activeCustomer.orders_count || 0} παραγγ.</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Σημείωση</div>
                    <div className="text-sm mt-1 text-slate-700 truncate">{activeCustomer.note || "—"}</div>
                  </div>
                </div>
              </Card>

              <Tabs defaultValue="addresses">
                <TabsList className="grid grid-cols-3 w-full mb-3 h-12">
                  <TabsTrigger value="addresses" className="text-base h-10" data-testid="delivery-customer-tab-addresses">
                    <MapPin size={16} className="mr-1.5" /> Διευθύνσεις
                  </TabsTrigger>
                  <TabsTrigger value="history" className="text-base h-10" data-testid="delivery-customer-tab-history">
                    <HistoryIcon size={16} className="mr-1.5" /> Ιστορικό
                  </TabsTrigger>
                  <TabsTrigger value="top" className="text-base h-10" data-testid="delivery-customer-tab-top">
                    <BarChart3 size={16} className="mr-1.5" /> Top Προϊόντα
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="addresses" className="space-y-2">
                  {(activeCustomer.addresses || []).filter((a) => a.status !== "deleted").length === 0 && (
                    <Card className="p-4 text-center text-slate-500 italic">Δεν υπάρχουν διευθύνσεις</Card>
                  )}
                  {(activeCustomer.addresses || [])
                    .filter((a) => a.status !== "deleted")
                    .map((a) => {
                      const selected = activeAddrId === a.id;
                      const paused = a.status === "paused";
                      return (
                        <button
                          key={a.id}
                          type="button"
                          disabled={paused}
                          onClick={() => setActiveAddrId(a.id)}
                          data-testid={`delivery-customer-addr-${a.id}`}
                          className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-all ${
                            selected
                              ? "bg-blue-50 border-blue-500 shadow-md"
                              : paused
                                ? "bg-amber-50 border-amber-300 opacity-60 cursor-not-allowed"
                                : "bg-white border-slate-200 hover:border-blue-300"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`mt-1 w-6 h-6 rounded-full border-2 grid place-items-center shrink-0 ${selected ? "border-blue-600 bg-blue-600" : "border-slate-300 bg-white"}`}>
                              {selected && <CheckCircle2 size={14} className="text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-lg font-bold flex items-center gap-2">
                                {[a.street, a.street_no].filter(Boolean).join(" ") || "—"}
                                {a.is_default && <Star size={14} className="text-yellow-500" fill="currentColor" />}
                                {paused && <span className="text-xs px-2 py-0.5 rounded bg-amber-200 text-amber-900 font-bold flex items-center gap-1"><Pause size={10} /> ΣΕ ΠΑΥΣΗ</span>}
                              </div>
                              <div className="text-sm text-slate-600 mt-0.5">
                                {a.area || "—"}
                                {a.floor && ` · όρ. ${a.floor}`}
                                {a.doorbell && ` · κορδ. ${a.doorbell}`}
                              </div>
                              {a.instructions && (
                                <div className="text-xs text-slate-500 italic mt-1 leading-snug">› {a.instructions}</div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </TabsContent>

                <TabsContent value="history">
                  <Card className="p-0 overflow-hidden">
                    <table className="w-full text-base">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-sm">Ημ/νία</th>
                          <th className="text-left px-3 py-2 text-sm">Τύπος</th>
                          <th className="text-right px-3 py-2 text-sm">Ποσό</th>
                          <th className="text-right px-3 py-2 text-sm w-32">Είδη</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(history?.items || []).map((it, i) => (
                          <tr
                            key={i}
                            className="border-t hover:bg-blue-50 cursor-pointer"
                            onClick={() => setPreviewReceipt(it)}
                            data-testid={`delivery-customer-history-row-${i}`}
                          >
                            <td className="px-3 py-3 font-mono text-sm">{(it.sent_at || "").slice(0, 19).replace("T", " ")}</td>
                            <td className="px-3 py-3 flex items-center gap-1.5 text-base"><Receipt size={14} /> {it.type || "—"}</td>
                            <td className="px-3 py-3 text-right font-mono font-bold text-lg">{Number(it.total_amount || 0).toFixed(2)}€</td>
                            <td className="px-3 py-3 text-right text-sm text-slate-500 font-mono">{(it.items || []).length}× ›</td>
                          </tr>
                        ))}
                        {(!history?.items || history.items.length === 0) && (
                          <tr><td colSpan={4} className="text-center py-6 text-slate-400 italic">Δεν υπάρχει ιστορικό.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </Card>
                  <div className="text-xs text-slate-400 italic mt-2">Κάντε κλικ σε μια απόδειξη για προεπισκόπηση και προσθήκη ειδών.</div>
                </TabsContent>

                <TabsContent value="top">
                  <Card className="p-3 space-y-2">
                    {(topItems?.items || []).length === 0 && (
                      <div className="text-center text-slate-400 italic py-4">Χωρίς ιστορικό προϊόντων.</div>
                    )}
                    {(topItems?.items || []).map((it, i) => {
                      const st = it._stats || {};
                      return (
                        <div key={i} className="flex items-center gap-3 px-2 py-3 border-b last:border-b-0">
                          <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 grid place-items-center font-bold text-lg">{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-lg truncate">{it.description}</div>
                            <div className="text-sm text-slate-500 font-mono">×{st.total_qty || 0} · {st.frequency || 0} παραγγ. · {Number(it.price || 0).toFixed(2)}€</div>
                          </div>
                          <div className="text-purple-700 font-mono font-bold text-sm">score {Number(st.score || 0).toFixed(0)}</div>
                          {onAddItem && (
                            <Button
                              size="lg"
                              onClick={() => { onAddItem(it); toast.success(`+ ${it.description}`); }}
                              className="h-12 bg-purple-600 hover:bg-purple-700 text-white font-bold shrink-0"
                              data-testid={`delivery-customer-top-add-${i}`}
                            >
                              <Plus size={18} className="mr-1" /> Προσθήκη
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Footer ---------------------------------------------- */}
          <div className="bg-white border-t-2 border-slate-300 px-5 py-3 shrink-0 flex items-center gap-3">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setStep("search")}
              className="h-12 border-2"
              data-testid="delivery-customer-confirm-back"
            >
              <ArrowLeft size={18} className="mr-1" /> Πίσω
            </Button>
            <div className="flex-1 text-sm">
              {activeAddrId ? (
                <span className="text-emerald-700 font-bold">✓ Επιλεγμένη διεύθυνση</span>
              ) : (
                <span className="text-amber-700 font-bold">Επιλέξτε διεύθυνση παράδοσης</span>
              )}
            </div>
            {/* Quick Repeat — shown when the customer has a prior order.
                Same address requirement applies. */}
            {lastOrder?.found && (
              <Button
                size="lg"
                onClick={() => confirm({ repeat: true })}
                disabled={!activeAddrId}
                className="h-12 bg-purple-600 hover:bg-purple-700 text-white font-bold disabled:opacity-40 leading-tight whitespace-normal text-left"
                data-testid="delivery-customer-quick-repeat"
                title={`Επιβεβαίωση + αυτόματη φόρτωση τελευταίας παραγγελίας (${(lastOrder.items || []).length} είδ.)`}
              >
                <Repeat2 size={18} className="mr-1.5 shrink-0" />
                <span className="flex flex-col items-start">
                  <span className="text-xs opacity-90">Επανάληψη Τελευταίας</span>
                  <span className="font-mono">{Number(lastOrder.total_amount || 0).toFixed(2)}€ · {(lastOrder.items || []).length} είδ.</span>
                </span>
              </Button>
            )}
            <Button
              size="lg"
              onClick={() => confirm()}
              disabled={!activeAddrId}
              className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-40"
              data-testid="delivery-customer-confirm"
            >
              <CheckCircle2 size={18} className="mr-1.5" /> Επιβεβαίωση & Επιστροφή
            </Button>
          </div>
        </>
      )}

      {/* In-dialog Numpad — quick numeric search (phones, codes). */}
      <Numpad
        open={numpad.open}
        label="Αναζήτηση (αριθμός)"
        value={numpad.value}
        allowDot={false}
        onChange={(v) => { setNumpad({ open: true, value: v }); setQ(v); }}
        onAccept={() => setNumpad({ open: false, value: "" })}
        onClose={() => setNumpad({ open: false, value: "" })}
      />

      {/* Receipt preview — opens when a history row is clicked. Shows
          item lines with optional «+ Προσθήκη» buttons (when the parent
          editor passes `onAddItem`). */}
      {previewReceipt && (
        <ReceiptPreview
          receipt={previewReceipt}
          onClose={() => setPreviewReceipt(null)}
          onAddItem={onAddItem ? (it) => { onAddItem(it); toast.success(`+ ${it.description}`); } : null}
        />
      )}

      {/* Inline customer-card editor (full-screen) — for «Νέος Πελάτης»
          or «Επεξεργασία» without leaving the Delivery flow. */}
      <CustomerDialog
        open={cardEdit.open}
        onOpenChange={(o) => setCardEdit((c) => ({ ...c, open: o }))}
        customer={cardEdit.customer}
        onSaved={async () => {
          // After save, refetch & advance. For NEW (no prior customer.id)
          // we look up the most recent customer matching the q OR last
          // edited; for EDIT we refetch the same id and stay in confirm.
          const wasEdit = !!cardEdit.customer?.id;
          if (wasEdit) {
            try {
              const r = await http.get(`/customers/${cardEdit.customer.id}`);
              setActiveCustomer(r.data);
              toast.success("Ενημερώθηκε η καρτέλα");
            } catch (e) { /* noop */ }
          } else {
            // Fresh customer — list newest first and auto-pick the top.
            try {
              const r = await http.get("/customers?limit=1");
              const fresh = (r.data?.items || [])[0];
              if (fresh) {
                await pickCustomer(fresh);
                toast.success(`Νέος πελάτης: ${fresh.name}`);
              }
            } catch (e) { /* noop */ }
          }
          setCardEdit({ open: false, customer: null });
        }}
      />
    </div>
  );
}

// ---------------- Receipt preview ----------------
function ReceiptPreview({ receipt, onClose, onAddItem }) {
  const items = receipt?.items || [];
  return (
    <div className="fixed inset-0 z-[85] bg-black/60 grid place-items-center p-6">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <div className="px-5 py-4 border-b-2 border-slate-200 sticky top-0 bg-white flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold flex items-center gap-2"><Receipt size={22} /> Απόδειξη {(receipt.sent_at || "").slice(0, 16).replace("T", " ")}</div>
            <div className="text-sm text-slate-500 mt-0.5 font-mono">{receipt.type || "—"} · {(items || []).length} είδη · σύνολο <span className="text-emerald-700 font-bold">{Number(receipt.total_amount || 0).toFixed(2)}€</span></div>
          </div>
          <Button variant="outline" size="lg" onClick={onClose} className="h-12 border-2" data-testid="delivery-customer-preview-close">
            <X size={18} className="mr-1" /> Κλείσιμο
          </Button>
        </div>
        <div className="p-5 space-y-2">
          {items.length === 0 && <div className="text-slate-400 italic text-center py-8">Χωρίς είδη.</div>}
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-3 border-2 border-slate-200 rounded-lg hover:border-blue-300" data-testid={`delivery-customer-preview-row-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-xl truncate">{it.description}</div>
                <div className="text-base text-slate-600 font-mono mt-0.5">{Number(it.price || 0).toFixed(2)}€ × {it.qty || 1}{it.free_text ? `  ›  ${it.free_text}` : ""}</div>
              </div>
              <div className="text-right shrink-0 mr-2">
                <div className="text-lg font-mono font-bold">{(Number(it.price || 0) * Number(it.qty || 1)).toFixed(2)}€</div>
              </div>
              {onAddItem && (
                <Button
                  size="lg"
                  onClick={() => onAddItem(it)}
                  className="h-14 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-base shrink-0"
                  data-testid={`delivery-customer-preview-add-${i}`}
                >
                  <Plus size={20} className="mr-1.5" /> Προσθήκη
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
